import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { RMS_TEACHER_USER_TYPE } from '../../../constants/userTypes';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import {
  apiGetHfpV4Form,
  apiRecordHfpV4ScreenEvents,
  apiSaveHfpV4Draft,
  apiSubmitHfpV4Form,
} from '../../utils/rmsApi';
import {
  buildSequence,
  deviceClass,
  isScreenAnswered,
  progressCounts,
  resumeIndex,
} from '../../utils/hfpV4Form';
import HfpV4Screen from './HfpV4Screen';
import '../../components/harbour/harbour.css';
import './hfpV4Form.css';

/*
 * RmsHfpV4FormPage (TDI-525)
 * --------------------------
 * The v4 strengths and needs form for a parent or carer, or for the school,
 * built from td-os documents/hfp-v4/specs/form-specification.md:
 *
 *  - One question a screen (C1), the comparison stem once as a heading.
 *  - Next does not move on until the screen has an answer or Not sure (D83);
 *    pressing it says so beside the question instead.
 *  - Every answer saves as it is given, and the page says so ("Saved just
 *    now"). "Come back later" saves and leaves; the form resumes at its first
 *    unanswered screen (section 8).
 *  - Every shown screen sends its open and answer times (section 9.2).
 *  - The order and what shows come from the bundle's predicates through
 *    utils/hfpV4Form; the browser never scores (D86).
 *  - Once every screen is answered, a check step sends the form, and the
 *    server checks it again.
 *
 * The primary actions live in a footer bar that is a sibling below the
 * scrolling question, so they never need scrolling to reach and never cover
 * the focused answer.
 */

const SAVE_DELAY_MS = 600;
const EVENT_BATCH = 20;

// A class teacher goes back to their pupil's page (TDI-620), never the
// SENCO's school forms, which are not theirs (TDI-618).
const returnRouteFor = (role, patientRecordId, userType) => {
  const id = encodeURIComponent(patientRecordId);
  if (userType === RMS_TEACHER_USER_TYPE) return `/rms/teacher/pupils/${id}`;
  return role === 'teacher' ? `/rms/pupils/${id}/forms/sen` : `/rms/family/child/${id}/forms/sen`;
};

const nowIso = () => new Date().toISOString();
const currentDevice = () => deviceClass(typeof window !== 'undefined' ? window.innerWidth : 1280);

function sectionLabel(entry, areasById) {
  if (!entry) return '';
  if (entry.type === 'question' && entry.screen.kind === 'context') return 'About them';
  if (entry.type === 'question') return (areasById[entry.screen.area] || {}).name || '';
  return 'Things around them';
}

export default function RmsHfpV4FormPage() {
  const { patientRecordId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;

  const [phase, setPhase] = useState('loading'); // loading | refused | error | form | check | sending | sent
  const [message, setMessage] = useState('');
  const [bundle, setBundle] = useState(null);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [showError, setShowError] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | failed
  const [sendError, setSendError] = useState('');

  const pending = useRef({});
  const saveTimer = useRef(null);
  const events = useRef([]);
  const openedAt = useRef(null);
  const answeredOnce = useRef(new Set());
  const headingRef = useRef(null);

  const sequence = useMemo(() => buildSequence(bundle, answers), [bundle, answers]);
  const entry = phase === 'form' ? sequence[index] : null;
  const areasById = useMemo(
    () => Object.fromEntries(((bundle && bundle.areas) || []).map((a) => [a.id, a])),
    [bundle],
  );
  const returnTo = (location.state && location.state.from)
    || returnRouteFor(bundle && bundle.role, patientRecordId, claims?.userType);

  // ── screen events (specification 9.2) ─────────────────────────────────────
  const flushEvents = useCallback(async () => {
    if (!orgId || events.current.length === 0) return;
    const batch = events.current.splice(0, events.current.length);
    try {
      await apiRecordHfpV4ScreenEvents({ orgId, patientRecordId, events: batch });
    } catch (err) {
      // Keep them for the next flush; timings are never worth blocking a parent.
      events.current = [...batch, ...events.current];
    }
  }, [orgId, patientRecordId]);

  const recordEvent = useCallback((event, at, extra = {}) => {
    const target = sequence[at];
    if (!target) return;
    events.current.push({
      screen_id: target.id,
      position: at,
      opened_at: openedAt.current || nowIso(),
      device_class: currentDevice(),
      event,
      ...extra,
    });
    if (events.current.length >= EVENT_BATCH) flushEvents();
  }, [sequence, flushEvents]);

  // ── saving ─────────────────────────────────────────────────────────────────
  const flushSave = useCallback(async (latestAnswers) => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const patch = pending.current;
    if (!orgId || Object.keys(patch).length === 0) return true;
    pending.current = {};
    setSaveState('saving');
    try {
      const seq = buildSequence(bundle, latestAnswers);
      await apiSaveHfpV4Draft({ orgId, patientRecordId, answers: patch, progress: progressCounts(seq, latestAnswers) });
      setSaveState('saved');
      return true;
    } catch (err) {
      pending.current = { ...patch, ...pending.current };
      setSaveState('failed');
      return false;
    }
  }, [orgId, patientRecordId, bundle]);

  const latest = useRef(answers);
  latest.current = answers;

  const onAnswer = useCallback((patch) => {
    setAnswers((prev) => {
      const next = { ...prev, ...patch };
      latest.current = next;
      return next;
    });
    pending.current = { ...pending.current, ...patch };
    setShowError(false);
    const first = !answeredOnce.current.has(entry && entry.id);
    if (entry) {
      answeredOnce.current.add(entry.id);
      const value = Object.values(patch)[0];
      recordEvent(first ? 'answer' : 'change', index, {
        answered_at: nowIso(),
        answer_id: typeof value === 'string' && entry.type !== 'grid' ? value : null,
      });
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flushSave(latest.current), SAVE_DELAY_MS);
  }, [entry, index, recordEvent, flushSave]);

  // ── load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!orgId || !patientRecordId) return;
    setPhase('loading');
    try {
      const res = await apiGetHfpV4Form({ orgId, patientRecordId });
      if (!res || !res.allocated || !res.bundle) {
        setMessage((res && res.message) || 'This form is not available at the moment.');
        setPhase('refused');
        return;
      }
      const saved = (res.draft && res.draft.answers) || {};
      setBundle(res.bundle);
      setAnswers(saved);
      Object.keys(saved).forEach((k) => answeredOnce.current.add(k.split(':')[0]));
      if (res.draft && res.draft.submitted) {
        setPhase('sent');
        return;
      }
      const seq = buildSequence(res.bundle, saved);
      const at = resumeIndex(seq, saved);
      setIndex(Math.min(at, Math.max(seq.length - 1, 0)));
      setPhase(at >= seq.length && seq.length > 0 ? 'check' : 'form');
      openedAt.current = nowIso();
      if (res.draft && res.draft.started && seq[at]) {
        events.current.push({
          screen_id: seq[at].id, position: at, opened_at: openedAt.current, device_class: currentDevice(), event: 'resume',
        });
      }
    } catch (err) {
      console.error('HFP v4 form failed to load:', err);
      setPhase('error');
    }
  }, [orgId, patientRecordId]);

  useEffect(() => { load(); }, [load]);

  // Each screen opening is an event, and focus moves to its question so a
  // screen reader reads the new screen. A layout effect, so the open is
  // recorded as the screen is shown and always before anything the person
  // does on it: a passive effect can run after their first click, and a
  // "Come back later" on a slow phone would then send its events without it.
  useLayoutEffect(() => {
    if (phase !== 'form' || !entry) return;
    openedAt.current = nowIso();
    recordEvent('open', index);
    if (headingRef.current) {
      const legend = headingRef.current.querySelector('legend, h2');
      if (legend && typeof legend.focus === 'function') legend.focus();
    }
    // Only a new screen opens one; answers on the same screen do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, entry && entry.id]);

  // A changed answer can close follow-on screens; never point past the end.
  useEffect(() => {
    if (phase === 'form' && sequence.length > 0 && index >= sequence.length) setIndex(sequence.length - 1);
  }, [phase, index, sequence.length]);

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  // ── navigation ─────────────────────────────────────────────────────────────
  const goNext = () => {
    if (!isScreenAnswered(entry, answers)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    if (index + 1 >= sequence.length) {
      flushSave(latest.current);
      flushEvents();
      setPhase('check');
      return;
    }
    setIndex(index + 1);
  };

  const goBack = () => {
    setShowError(false);
    if (phase === 'check') {
      setPhase('form');
      setIndex(Math.max(sequence.length - 1, 0));
      return;
    }
    if (index === 0) return;
    recordEvent('back', index);
    setIndex(index - 1);
  };

  const comeBackLater = async () => {
    recordEvent('save_later', index);
    await flushSave(latest.current);
    await flushEvents();
    navigate(returnTo);
  };

  const send = async () => {
    setSendError('');
    setPhase('sending');
    const saved = await flushSave(latest.current);
    if (!saved) {
      setSendError('Your answers could not be saved. Check your connection and try again.');
      setPhase('check');
      return;
    }
    events.current.push({
      screen_id: 'submit', position: sequence.length, opened_at: nowIso(), device_class: currentDevice(), event: 'submit',
    });
    await flushEvents();
    try {
      await apiSubmitHfpV4Form({ orgId, patientRecordId });
      setPhase('sent');
    } catch (err) {
      const unanswered = sequence.findIndex((e) => !isScreenAnswered(e, latest.current));
      if (unanswered >= 0) {
        setIndex(unanswered);
        setPhase('form');
        setShowError(true);
        return;
      }
      setSendError('Your answers could not be sent. They are saved, so you can try again.');
      setPhase('check');
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  const counts = progressCounts(sequence, answers);
  const saveLine = {
    idle: '',
    saving: 'Saving',
    saved: 'Saved just now',
    failed: 'Not saved yet. We will try again when you answer.',
  }[saveState];

  if (phase === 'loading') {
    return (
      <div className="hfp4-page"><p className="hfp4-status" role="status">Loading the form</p></div>
    );
  }
  if (phase === 'error') {
    return (
      <div className="hfp4-page">
        <div className="hfp4-panel" role="alert">
          <h1 className="hfp4-title">The form could not be opened</h1>
          <p className="hfp4-body">Check your connection and try again. Nothing you answered before is lost.</p>
          <HarbourButton kind="family" onClick={load}>Try again</HarbourButton>
        </div>
      </div>
    );
  }
  if (phase === 'refused') {
    return (
      <div className="hfp4-page">
        <div className="hfp4-panel" role="status">
          <h1 className="hfp4-title">Strengths and needs profile</h1>
          <p className="hfp4-body">{message}</p>
          <HarbourTextLink size="standard" onClick={() => navigate(returnTo)}>Go back</HarbourTextLink>
        </div>
      </div>
    );
  }
  if (phase === 'sent') {
    return (
      <div className="hfp4-page">
        <div className="hfp4-panel hfp4-panel--done" role="status">
          <h1 className="hfp4-title">Thank you, your answers are in</h1>
          <p className="hfp4-body">
            That was a lot of questions, and every answer helps. When home and school have both answered, the profile is put together.
          </p>
          <p className="hfp4-body">You do not need to do anything else for this form.</p>
          <HarbourButton kind="family" onClick={() => navigate(returnTo)}>Back to your forms</HarbourButton>
        </div>
      </div>
    );
  }
  if (phase === 'check' || phase === 'sending') {
    return (
      <div className="hfp4-page">
        <div className="hfp4-panel">
          <h1 className="hfp4-title">Check and send</h1>
          <p className="hfp4-body">You have answered all {sequence.length} questions.</p>
          <p className="hfp4-body">Your answers are saved. Send them when you are ready.</p>
          {sendError ? <p className="hfp4-error" role="alert">{sendError}</p> : null}
          <div className="hfp4-panel__actions">
            <HarbourButton kind="family" busy={phase === 'sending'} busyLabel="Sending" disabled={phase === 'sending'} onClick={send}>
              Send your answers
            </HarbourButton>
            <HarbourButton kind="secondary" onClick={goBack} disabled={phase === 'sending'}>Back</HarbourButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hfp4-page hfp4-page--form">
      <header className="hfp4-top">
        <p className="hfp4-section">{sectionLabel(entry, areasById)}</p>
        <p className="hfp4-count">
          Question {index + 1} of {sequence.length}
        </p>
      </header>
      <main className="hfp4-scroll" ref={headingRef}>
        <HfpV4Screen entry={entry} answers={answers} onAnswer={onAnswer} showError={showError} />
      </main>
      <footer className="hfp4-bar">
        <p className="hfp4-save" role="status" aria-live="polite">
          {saveLine}
          {counts.answered > 0 && saveState !== 'failed' ? <span className="hfp4-save__caption"> Your answers save as you go.</span> : null}
        </p>
        <div className="hfp4-bar__actions">
          <HarbourButton kind="secondary" onClick={goBack} disabled={index === 0}>Back</HarbourButton>
          <HarbourButton kind="family" onClick={goNext}>Next</HarbourButton>
        </div>
        <HarbourTextLink size="standard" onClick={comeBackLater}>Come back later</HarbourTextLink>
      </footer>
    </div>
  );
}
